// One trial's burn-down (SCR-009): credit and tokens remaining per day and the projected end, as
// a table (the accessible form of the burn-down; "chart has a table alternative").
import { useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import type { ApiError } from "@nexus/ui";
import { ErrorBanner, Loading } from "@nexus/ui";
import { burndownDayRow, burndownSummary, loadBurndown, type Burndown } from "./model";

type State = { kind: "loading" } | { kind: "error"; error: ApiError } | { kind: "loaded"; burndown: Burndown };

export function TrialBurndown({ orgId, trialId }: { orgId: string; trialId: string }) {
  const client = useApi();
  const [state, setState] = useState<State>({ kind: "loading" });

  const load = () => {
    setState({ kind: "loading" });
    void loadBurndown(client, orgId, trialId).then((result) => {
      if ("burndown" in result) setState({ kind: "loaded", burndown: result.burndown });
      else setState({ kind: "error", error: result.error });
    });
  };

  useEffect(load, [client, orgId, trialId]);

  if (state.kind === "loading") return <Loading label="Loading burn-down…" />;
  if (state.kind === "error") return <ErrorBanner error={state.error} onRetry={load} />;

  const { burndown } = state;
  return (
    <div class="trial-burndown stack-sm">
      <p class="card-note">{burndownSummary(burndown)}</p>
      <div class="table-scroll">
        <table class="data-table data-table-compact">
          <caption>Burn-down by day</caption>
          <thead>
            <tr>
              <th scope="col">Day</th>
              <th scope="col" class="numeric">
                Credited
              </th>
              <th scope="col" class="numeric">
                Tokens
              </th>
              <th scope="col" class="numeric">
                Credit left
              </th>
              <th scope="col" class="numeric">
                Tokens left
              </th>
            </tr>
          </thead>
          <tbody>
            {burndown.days.length === 0 && (
              <tr>
                <td colSpan={5}>No usage drawn on this trial yet.</td>
              </tr>
            )}
            {burndown.days.map((day) => {
              const row = burndownDayRow(day);
              return (
                <tr key={row.day}>
                  <td>{row.day}</td>
                  <td class="numeric" title={row.priceCreditedDisplay.exact}>
                    {row.priceCreditedDisplay.text}
                  </td>
                  <td class="numeric">{row.tokensCounted.toLocaleString("en-US")}</td>
                  <td class="numeric">{row.creditRemainingDisplay ? row.creditRemainingDisplay.text : "—"}</td>
                  <td class="numeric">{row.tokensRemaining === null ? "—" : row.tokensRemaining.toLocaleString("en-US")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
